/**
 * Унифицированный API-клиент для админ-панели
 * Работает только через same-origin /api с cookie-based session auth.
 */

const API_BASE = '/api';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface Route {
  id: string;
  routeNumber: string;
  name: string;
  description?: string;
  color: string;
  isActive: boolean;
}

export interface Bus {
  id: string;
  plateNumber: string;
  model: string;
  capacity: number;
  status: 'active' | 'maintenance' | 'retired';
}

export type TripStatus = 'scheduled' | 'boarding' | 'departed' | 'delayed' | 'cancelled';

export interface Trip {
  id: string;
  routeId: string;
  busId: string;
  startTime: string;
  direction: 'forward' | 'backward';
  calendarId: string;
  status: TripStatus;
  delayMinutes?: number;
  platform?: string;
}

export interface TripWithDetails extends Trip {
  routeName?: string;
  routeNumber?: string;
  routeColor?: string;
  busPlate?: string;
  busModel?: string;
}

export interface SessionUser {
  id: string;
  username: string;
  displayName: string;
  roles: Array<'admin' | 'editor'>;
  forcePasswordChange: boolean;
  mfaEnabled: boolean;
}

interface RequestConfig extends RequestInit {
  params?: Record<string, string>;
  skipCsrf?: boolean;
}

let csrfTokenCache: string | null = null;

function isUnsafeMethod(method?: string): boolean {
  const normalized = (method || 'GET').toUpperCase();
  return !['GET', 'HEAD', 'OPTIONS'].includes(normalized);
}

async function getCsrfToken(): Promise<string> {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }
  const response = await request<{ csrfToken: string }>('/auth/csrf', {
    method: 'GET',
    skipCsrf: true,
  });
  csrfTokenCache = response.csrfToken;
  return response.csrfToken;
}

export function clearCachedCsrfToken(): void {
  csrfTokenCache = null;
}

export function setCachedCsrfToken(value: string): void {
  csrfTokenCache = value;
}

async function request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
  const { params, skipCsrf = false, ...init } = config;
  const url = new URL(`${API_BASE}${endpoint}`, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, value);
      }
    });
  }

  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }

  if (isUnsafeMethod(init.method) && !skipCsrf) {
    headers.set('x-csrf-token', await getCsrfToken());
  }

  const response = await fetch(url.toString(), {
    ...init,
    credentials: 'same-origin',
    headers,
  });

  const payload = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!response.ok || !payload?.success) {
    const message = payload?.message || `API Error ${response.status}`;
    if (response.status === 401) {
      clearCachedCsrfToken();
    }
    throw new ApiError(response.status, message);
  }

  return payload.data;
}

export async function login(username: string, password: string): Promise<{ user: SessionUser; csrfToken: string }> {
  const result = await request<{ user: SessionUser; csrfToken: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
    skipCsrf: true,
  });
  setCachedCsrfToken(result.csrfToken);
  return result;
}

export async function logout(): Promise<void> {
  await request('/auth/logout', { method: 'POST' });
  clearCachedCsrfToken();
}

export const getCurrentUser = (): Promise<SessionUser> =>
  request<SessionUser>('/auth/me', { skipCsrf: true });

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ user: SessionUser; csrfToken: string }> {
  const result = await request<{ user: SessionUser; csrfToken: string }>('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  setCachedCsrfToken(result.csrfToken);
  return result;
}

export const getRoutes = (): Promise<Route[]> =>
  request<Route[]>('/routes');

export const getRoute = (id: string): Promise<Route> =>
  request<Route>(`/routes/${id}`);

export const createRoute = (data: Omit<Route, 'id'>): Promise<Route> =>
  request<Route>('/routes', { method: 'POST', body: JSON.stringify(data) });

export const updateRoute = (id: string, data: Partial<Route>): Promise<Route> =>
  request<Route>(`/routes/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const deleteRoute = (id: string): Promise<void> =>
  request<void>(`/routes/${id}`, { method: 'DELETE' });

export const getBuses = (): Promise<Bus[]> =>
  request<Bus[]>('/buses');

export const getBus = (id: string): Promise<Bus> =>
  request<Bus>(`/buses/${id}`);

export const createBus = (data: Omit<Bus, 'id'>): Promise<Bus> =>
  request<Bus>('/buses', { method: 'POST', body: JSON.stringify(data) });

export const updateBus = (id: string, data: Partial<Bus>): Promise<Bus> =>
  request<Bus>(`/buses/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const deleteBus = (id: string): Promise<void> =>
  request<void>(`/buses/${id}`, { method: 'DELETE' });

export const getTrips = (): Promise<Trip[]> =>
  request<Trip[]>('/trips');

export const getTrip = (id: string): Promise<Trip> =>
  request<Trip>(`/trips/${id}`);

export const createTrip = (data: Omit<Trip, 'id'>): Promise<Trip> =>
  request<Trip>('/trips', { method: 'POST', body: JSON.stringify(data) });

export const updateTrip = (id: string, data: Partial<Trip>): Promise<Trip> =>
  request<Trip>(`/trips/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

export const deleteTrip = (id: string): Promise<void> =>
  request<void>(`/trips/${id}`, { method: 'DELETE' });

export const getSchedule = async (date: string): Promise<TripWithDetails[]> => {
  const payload = await request<{ date: string; trips: TripWithDetails[] }>('/schedule', {
    params: { date },
  });
  return payload.trips;
};
